import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useForm } from 'react-hook-form';

import { InferType } from 'yup';
import { editContactInformationSchema } from '../schemas';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  useBuildingsShortQuery,
  useEditPatientContactInformationMutation,
  usePatientContactInformationQuery,
} from '@common/cache';
import { paths } from '@common/types';
import { formatApiError } from '@common/errors/error-formatting.ts';

const languageOptions = [
  'English',
  'Spanish',
  'Chinese',
  'Arabic',
  'French',
  'Portuguese',
  'Russian',
  'Tagalog (Filipino)',
  'Other',
];

type IFormShape = InferType<typeof editContactInformationSchema>;

const initialValues: IFormShape = {
  type: 'Patient',
  do_not_call: false,
  language: 'English',
  phone_number: '',
  email: '',
  mobile_phone_number: '',
};

export const useEditContactInformation = (patient_id: string) => {
  const patientContactInformation = usePatientContactInformationQuery({
    patient_id,
  });
  const { data: buildingOptions = [] } = useBuildingsShortQuery({});

  const formMethods = useForm<IFormShape>({
    mode: 'onChange',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver<IFormShape, any, any>(editContactInformationSchema),
    defaultValues: initialValues,
  });

  const {
    handleSubmit,
    formState: { isSubmitting, isDirty, isValid },
    reset,
  } = formMethods;

  const { mutateAsync, isPending } = useEditPatientContactInformationMutation({
    patient_id,
  });
  const [isOpen, setIsOpen] = useState(false);
  const [isSaveChangesModalOpen, setIsSaveChangesModalOpen] = useState(false);

  const handleClose = () => {
    if (isDirty) {
      setIsSaveChangesModalOpen(true);
    } else {
      setIsOpen(false);
    }
  };

  const handleOnClick = () => {
    setIsOpen(true);
  };

  const submitHandler = async (values: IFormShape) => {
    try {
      await mutateAsync({
        type: values.type as paths['/patient/{patient_id}/contact-information']['patch']['requestBody']['content']['application/json']['type'],
        email: values.email || undefined,
        phone_number: values.phone_number || undefined,
        mobile_phone_number: values.mobile_phone_number || undefined,
        language:
          (values.language as paths['/patient/{patient_id}/contact-information']['patch']['requestBody']['content']['application/json']['language']) ||
          undefined,
        do_not_call: values.do_not_call || false,
      });

      setIsSaveChangesModalOpen(false);
      setIsOpen(false);
      reset(values);
      toast.success('Patient edited successfully');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const onSubmit = handleSubmit(submitHandler);

  const handleCloseSaveChangesModal = () => {
    setIsSaveChangesModalOpen(false);
  };

  const handleDiscardChanges = () => {
    setIsSaveChangesModalOpen(false);
    setIsOpen(false);
    reset();
  };

  const handleSaveChanges = async () => {
    await onSubmit();
  };

  useEffect(() => {
    if (patientContactInformation.data) {
      const {
        caregiver_contact_information,
        patient_contact_information,
        type,
      } = patientContactInformation.data;

      reset({
        type,
        do_not_call:
          type === 'Patient'
            ? patient_contact_information.do_not_call
            : caregiver_contact_information.do_not_call,
        language:
          type === 'Patient'
            ? patient_contact_information.language
            : caregiver_contact_information.language,
        phone_number:
          type === 'Patient'
            ? patient_contact_information.phone_number
            : caregiver_contact_information.phone_number,
        email:
          type === 'Patient'
            ? patient_contact_information.email
            : caregiver_contact_information.email,
        mobile_phone_number:
          type === 'Patient'
            ? patient_contact_information.mobile_phone_number
            : caregiver_contact_information.mobile_phone_number,
      });
    }
  }, [patientContactInformation.data]);

  return {
    formMethods,
    onSubmit,
    isSubmitting,
    isOpen,
    submitHandler,
    handleSubmit,
    handleClose,
    handleOnClick,
    languageOptions: languageOptions.map(o => ({ value: o, label: o })),
    buildingOptions,
    patientContactInformation: patientContactInformation.data,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleSaveChanges,
    handleDiscardChanges,
    isValid,
    isEditSubmitting: isPending,
  };
};
